/**
 * The shapes service-voyager-api answers with, and the decisions the client
 * makes from them. Kept free of the API client (which touches `window` at
 * import time) so it can be unit-tested directly.
 *
 * Every non-2xx body is `{ status: [{ type, message?, fields? }] }`
 * (utils/api/respond.ts `errorResponse`); a 2xx body never carries `status`.
 */

export type ApiStatus = { type: string; message?: string; fields?: string[] };

/** The one error body the API sends. */
export type ApiErrorBody = { status: ApiStatus[] };

/** The 401 types (utils/api/Type.ts `getHttpCode`). */
export const ACCESS_TOKEN_EXPIRED = 'access_token_expired';
export const INVALID_AUTH = 'invalid_auth';
export const MFA_LOCKOUT = 'mfa_lockout';

const isApiStatus = (value: unknown): value is ApiStatus =>
  !!value && typeof value === 'object' && typeof (value as ApiStatus).type === 'string';

/** The `status` entries of an error body; none for anything else. */
export const getStatuses = (body: unknown): ApiStatus[] => {
  const status = (body as Partial<ApiErrorBody> | null | undefined)?.status;
  return Array.isArray(status) ? status.filter(isApiStatus) : [];
};

/** Every message an error body carries, in order; a bulk import sends one per bad row. */
export const getStatusMessages = (body: unknown): string[] =>
  getStatuses(body)
    .map(({ message }) => message)
    .filter((message): message is string => !!message);

export const hasStatusType = (body: unknown, type: string): boolean =>
  getStatuses(body).some((status) => status.type === type);

/**
 * Why a response ends the signed-in session, or null when it does not.
 *
 * - `expired`: the session outlived its 180 days. Always ends it, whatever
 *   the caller asked: the token will never work again.
 * - `unauthorized`: any other 401 (`invalid_auth`, `mfa_lockout`, or a type
 *   this client does not know). A request that carried an access token always
 *   ends the session on it: the API answers a revoked, signed-out or unknown
 *   token with 401 `invalid_auth` from every endpoint (Auth.ts), and a wrong
 *   confirm-password is a 400, never a 401. Only a request sent without a
 *   token — the sign-in screens, where a 401 is an answer about the
 *   credentials typed — may opt out with `logoutIfNotAuthorized: false`.
 *
 * Anything but a 401 leaves the session alone.
 */
export type SessionEnd = 'expired' | 'unauthorized';

export const getSessionEnd = (
  httpStatus: number,
  body: unknown,
  {
    logoutIfNotAuthorized = true,
    sentAccessToken = false,
  }: { logoutIfNotAuthorized?: boolean; sentAccessToken?: boolean } = {}
): SessionEnd | null => {
  if (httpStatus !== 401) return null;
  if (hasStatusType(body, ACCESS_TOKEN_EXPIRED)) return 'expired';
  return logoutIfNotAuthorized || sentAccessToken ? 'unauthorized' : null;
};

/**
 * A success body, or null when there is none: the API answers an action with
 * nothing to report (logout, revoke, send-mfa, request-password-reset,
 * reset-password) with 204 and an empty body.
 */
export const readJsonBody = async <T>(response: Response): Promise<T | null> => {
  if (response.status === 204) return null;
  const text = await response.text();
  return text ? (JSON.parse(text) as T) : null;
};
